import React, { useContext, useEffect, useState } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import { Layout, theme as antTheme } from 'antd';

import DetailSpinner from '@totem/components/common/DetailSpinner';
import ReportsContainer from '@totem/components/reports/ReportsContainer';
import Forbidden from '@totem/components/ui_components/pageExceptions/forbidden/Forbidden';
import NotFound from '@totem/components/ui_components/pageExceptions/notFound/NotFound';
import ContractedServicesContext from '@totem/components/userContext/contractedServicesContext/contractedServicesContext';
import { getBaseRoutes } from '@totem/routes/routes';
import { LoginProfile } from '@totem/types/user';
import { LOGIN_PROFILE_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';

import Header from './header/Header';
import LeftNav from './ui_components/menus/LeftNav';
import UserProfileContext from './userContext/UserProfileContext';
import { RouteContextProvider } from './RouteContext';

import './base.css';

const { Content } = Layout;

const OrganizationRouteRedirect = () => {
  const orgMatch = useMatch('/dashboard/:organizationId/*');
  const organizationId = orgMatch?.params?.organizationId;
  const { userProfile, setProfileAndToken, getApiToken, setTokenTTL } =
    useContext(UserProfileContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!organizationId || !userProfile?.id) {
      return;
    }

    if (!/^[a-f0-9]{24}$/i.test(organizationId)) {
      return;
    }

    const targetPath = location.pathname.replace(
      new RegExp(`^/dashboard/${organizationId}`),
      '/dashboard',
    );
    const target = `${targetPath}${location.search}${location.hash}`;

    const navigateToTarget = () => {
      navigate(target, { replace: true });
    };

    if (userProfile.organization?.id === organizationId) {
      navigateToTarget();
      return;
    }

    setSwitching(true);

    fetch(
      `${LOGIN_PROFILE_ENDPOINT}/${userProfile.id}/${organizationId}/switch`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        if (res.status >= 400) {
          logger.error(res.statusText);
        }
        return res.json();
      })
      .then((result: LoginProfile) => {
        if (result.token !== '') {
          if (result.ttlMinutes > 0) {
            setTokenTTL(result.ttlMinutes);
          }
          setProfileAndToken(
            result.profile,
            result.token,
            'OrganizationRouteRedirect',
          );
        }
      })
      .finally(() => {
        setSwitching(false);

        navigateToTarget();
      });
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    organizationId,
    setProfileAndToken,
    userProfile?.id,
    userProfile?.organization?.id,
    getApiToken,
    organizationId,
  ]);

  if (switching) {
    return <DetailSpinner />;
  }

  return null;
};

const Base = () => {
  const { token } = antTheme.useToken();
  const { userProfile, mainMenuLocation } = useContext(UserProfileContext);
  const { data } = useContext(ContractedServicesContext);

  const styles = {
    content: {
      backgroundColor: token.colorBgContainer,
      display: 'flex',
      flexDirection: 'column' as any,
    },
    sider: {
      background: token.colorBgContainer,
      borderRight: `2px solid ${token.colorBorder}`,
    },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <ReportsContainer>
        {mainMenuLocation === 'Left' && (
          <div style={styles.sider} styleName="no-print">
            <LeftNav styleName="no-print" />
          </div>
        )}
        <Layout>
          <Header />
          <Content style={styles.content}>
            <OrganizationRouteRedirect />
            <Routes>
              {getBaseRoutes(userProfile).map((route) => {
                const enabled =
                  typeof route.render === 'function'
                    ? route.render(userProfile, data)
                    : (route.render ?? true);

                if (enabled) {
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <RouteContextProvider value={route}>
                          <route.component />
                        </RouteContextProvider>
                      }
                    />
                  );
                }

                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<Forbidden />}
                  />
                );
              })}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Content>
        </Layout>
      </ReportsContainer>
    </Layout>
  );
};

export default Base;
